import { Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import FullScreenDialog from '../full-screen-dialog/FullScreenDialog';
import SettingsBar, { SettingsActionEnum } from '../settings-bar/SettingsBar';
import UnsavedChangesPrompt from '../unsaved-changes-prompt/UnsavedChangesPrompt';
import Form from '../forms/form/Form';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useUserSettings } from '../../queries/user-settings/UseUserSettings';
import { useSaveUserSettings } from '../../queries/user-settings/UseSaveUserSettings';
import { useOfflineConfig } from '../../queries/offline-config/UseOfflineConfig';
import { useForm } from 'react-hook-form';
import { UserStateActionTypeEnum, useUserContext } from '../../contexts/user-context/UserContext';
import { useUserDefaultViews } from '../../queries/user-default-view/UseUserDefaultViews';
import { UIViewEnum } from '../../common/enums/UIViewEnum';
import { eLynxObjectTypeEnum } from '../../common/enums/eLynxObjectType';
import AccountSettingsV2Utils, { AccountSettingsV2FormType, defaultAccountSettingsV2 } from './AccountSettingsV2Utils';
import AppearanceCard from './AppearanceCard';
import DefaultViewsCard from './DefaultViewsCard';
import { IUserSettings } from '../../services/user-settings-data/Models';
import { AppSettingsActionTypeEnum, useAppSettings } from '../../contexts/app-context/AppContext';
import { useSaveUserDefaultView } from '../../queries/user-default-view/UseSaveUserDefaultView';
import GaugeSheetDefaultsCard from './GaugeSheetDefaultsCard';
import GraphingDefaultsCard from './GraphingDefaultsCard';
import { useDeleteUserDefaultView } from '../../queries/user-default-view/UseDeleteUserDefaultView';
import SummaryListingCard from './SummaryListingCard';

export type AccountSettingsV2Props = {
    open: boolean;
    onClose: () => void
};

export default function AccountSettingsV2({open, onClose}: AccountSettingsV2Props) {
    
    const { t } = useTranslation();
    const [offlineState] = useOfflineDetectContext();
    const { confirm, acknowledge } = useConfirmation();
    const [userState, dispatchUserState] = useUserContext();
    const [appSettings, dispatchAppSettings] = useAppSettings();    
    const { data: userSettings } = useQueryNotifications(useUserSettings)({
        enabled: !offlineState.offline
    });
    const { mutate: saveUserSettings } = useQueryNotifications(useSaveUserSettings)({});


    const { data: offlineConfig } = useQueryNotifications(useOfflineConfig)({});


    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
        enabled: !offlineState.offline
    });

    const { mutate: saveUserDefaultView } = useQueryNotifications(useSaveUserDefaultView)({});
        const { mutate: deleteUserDefaultView } = useQueryNotifications(useDeleteUserDefaultView)({
            // onSuccess: () => {
            //     acknowledge(t("deleteSuccessMessage", { itemName: t("dataTypeNames.runTicket") }));
            // }
        });
    

    const form = useForm<AccountSettingsV2FormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {...defaultAccountSettingsV2
        }
    });

    const {
        getValues,
        //watch,
        //trigger,
        formState: {
            dirtyFields,
            isDirty,
            isValid,
            isSubmitting
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        //control,
        //handleSubmit,
    } = form;


    const onCloseSettings = async (action: SettingsActionEnum): Promise<void> => {

        if (action === SettingsActionEnum.OK) {

            const formValues = getValues();


            if (AccountSettingsV2Utils.hasPreferenceChanges(dirtyFields)) {

                const model = AccountSettingsV2Utils.mapToPreferencesModel(formValues, userSettings as IUserSettings);

                saveUserSettings(model);

                // saveOfflineConfig({
                //     ...offlineConfig,
                //     enabled: formValues.offlineMode ?? false
                // } as OfflineConfigSettings);


                if (formValues.theme !== appSettings.themeSet.mode.toString()) {
                    dispatchAppSettings({ 
                        type: AppSettingsActionTypeEnum.ThemeSetMode, 
                        mode: formValues.theme === 'light' ? 
                            'light' : 
                            'dark' 
                    });
                }

                if (dirtyFields?.defaultDeviceView) {

                    const currentDefaultView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device &&
                                                    !o.objectID && 
                                                    o.user?.id === userState.user?.id);

                    if (currentDefaultView && formValues.defaultDeviceView === UIViewEnum.None) {
                        deleteUserDefaultView(currentDefaultView.id);
                    } else {
                        saveUserDefaultView({
                            id: currentDefaultView?.id ?? -1,
                            user: userState.user,
                            objectID: undefined,
                            objectType: eLynxObjectTypeEnum.Device,
                            viewID: formValues.defaultDeviceView
                        });
                    }
                }

                if (dirtyFields?.defaultGroupView) {

                    const currentDefaultView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group &&
                                                    !o.objectID && 
                                                    o.user?.id === userState.user?.id);

                    //if (currentDefaultView && formValues.defaultGroupView === UIViewEnum.Group_SummaryListing) {
                    if (currentDefaultView && formValues.defaultGroupView === UIViewEnum.None) {
                        deleteUserDefaultView(currentDefaultView.id);
                    } else {

                    saveUserDefaultView({
                        id: currentDefaultView?.id ?? -1,
                        user: userState.user,
                        objectID: undefined,
                        objectType: eLynxObjectTypeEnum.Group,
                        viewID: formValues.defaultGroupView
                    });
                    }
                }


                dispatchUserState({
                    type: UserStateActionTypeEnum.UserState,
                    userState: {
                        ...userState,
                        userSettings: model as IUserSettings
                    }
                });

            }

            acknowledge(t('profile.appPreferencesUpdated'));

            reset({...defaultAccountSettingsV2}, {
                keepDirty: false, 
                keepDirtyValues: false
            });


        } else if (isDirty) {
            const result = await getUserConfirmation();
            if (!result)  {
                return;
            }
        }

        onClose();        
    }

    const getUserConfirmation = async () => {

        return (await confirm({
            dialogTitle: t('cancelConfirmation.title'),
            confirmationText: t('profile.cancelEditText'),
            yesButtonText: t('cancelConfirmation.yesButtonText'),
            noButtonText: t('cancelConfirmation.noButtonText'),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    };


    useEffect(() => {

        if (open) {

            let formData: AccountSettingsV2FormType | undefined = undefined;

            if (offlineState.offline && userState.user && offlineConfig) {

                formData = AccountSettingsV2Utils.mapToForm(undefined,
                    offlineConfig,
                    UIViewEnum.Device_Details,
                    UIViewEnum.Group_SummaryListing
                );

            } else if (userState.user && userSettings && offlineConfig) {

                const defaultDeviceView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                                                o.user?.id === userState.user?.id) ??
                                            defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                                                    !o.user);

                const defaultGroupView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                                            o.user?.id === userState.user?.id && !o.objectID) ??
                                        defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                                            !o.user && !o.objectID);
    

                formData = AccountSettingsV2Utils.mapToForm(
                    userSettings,
                    offlineConfig,
                    defaultDeviceView?.viewID ?? UIViewEnum.None,
                    defaultGroupView?.viewID ?? UIViewEnum.None);
            }

            if (formData) {
                reset(formData, {
                    keepDirty: true, 
                    keepDirtyValues: true
                });
            }
        }
    }, [
        userState.user,
        userSettings,
        //userRecipients,
        reset,
        offlineConfig,
        offlineState.offline,
        open,
        defaultViews
    ]);


    return (
        <FullScreenDialog open={open} onClose={onClose}>
            <SettingsBar title={t('profile.appPreferences')} 
                onClose={onCloseSettings}
                modified={isDirty && isValid  && !isSubmitting} />
            <Form form={form}
                sx={{
                    backgroundColor: "custom.background.cardContainer",
                }}
            >
                <Typography sx={{
                    padding: 1
                }} >
                    {t('profile.prefDesc')}

                </Typography>

            <Grid container spacing={2}>

                <Grid size={{ sm: 12, md: 4 }}>
                    <AppearanceCard />
                </Grid>

                <Grid size={{ sm: 12, md: 4 }}>
                    <DefaultViewsCard />

                    <SummaryListingCard />
                </Grid>

                <Grid size={{ sm: 12, md: 4 }}>
                    <GaugeSheetDefaultsCard />

                    <GraphingDefaultsCard />
                </Grid>


            </Grid>

            </Form>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </FullScreenDialog>
    );
  }
